import type { ReactNode } from 'react';
import React, { useCallback, useState } from 'react';

import HelpRexOutlineIcon from '@travel/design-system/assets/icons/HelpRexOutlineIcon';
import Button from '@travel/design-system/core/src/components/Button';
import IconButton from '@travel/design-system/core/src/components/IconButton';
import NotificationBanner from '@travel/design-system/core/src/components/NotificationBanner';
import {
  ButtonSize,
  ButtonVariantEnum,
  NotificationBannerStates,
  NotificationBannerToastTimers,
  NotificationBannerTypes,
} from '@travel/design-system/core/src/types/variants';
import { L10nNumber } from '@travel/i18n';
import cx from '@travel/icons/_utils/classnames';
import { DefaultProfile, EditVariant, Link as PreviewLink, ShareLarge } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import Media from '@travel/traveler-core/components/Media';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { IconTextLink } from '@travel/ui';

import { translateCompactNumberUnit } from 'constants/compactNumberLabels';
import { useRatTagContext } from 'context/ratTagContext';
import paths from 'core/universalRouter/paths';
import { getIsViewingOtherUser, getOtherUserId } from 'store/profileById/selector';
import { getDefaultSNSList } from 'store/snsDefaultList/selector';
import { getUserId } from 'store/userProfile/selectors';
import bindClickRat from 'utils/bindClickRat';
import { generateRatId } from 'utils/ratHelper';
import { getSNSIcons } from 'utils/snsIcons';
import EditButton from '../EditButton';

import { pushLocation } from 'store/__router/actions';

import { DEFAULT_REVIEW_HOST, MY_REVIEW_Q_AND_A_URL } from 'constants/common';
import { MediaType } from 'store/apiTypes';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import styles from './profileDetails.module.scss';

import type { UserProfile, UserProfileSns } from 'store/apiTypes';
type ProfileDetailsProps = UserProfile & {
  handleOpenSNS?: () => void;
};

function ProfileStatItem({
  label,
  count,
  testId,
}: {
  label: ReactNode;
  count?: number;
  testId?: string;
}) {
  return (
    <div className={styles.elementCount}>
      <p className={styles.count} data-testid={testId}>
        {count ? (
          <>{<L10nNumber value={count} isCompact translateUnit={translateCompactNumberUnit} />}</>
        ) : (
          '-'
        )}
      </p>
      <p className={styles.label}>{label}</p>
    </div>
  );
}

function UserProfileSNS({
  snsLinks,
  handleOpenSNS,
}: {
  snsLinks?: UserProfileSns[];
  handleOpenSNS?: () => void;
}) {
  const { isPC } = useDeviceTypes();
  const isViewingOtherUser = useAppSelector(getIsViewingOtherUser);
  const ratTagContext = useRatTagContext();
  const { ratTagValues } = ratTagContext;
  const snsList = useAppSelector(getDefaultSNSList);

  const SNS_ICON_LIST = getSNSIcons(12);

  const ratLinkRef = useCallback((element) => {
    bindClickRat(element);
  }, []);

  const handleSnsUrl = (url) => {
    if (url) window.open(url, '_blank');
  };

  if (isViewingOtherUser && !snsLinks?.length) return null;

  const getSNSId = (url, platform) => {
    const snsItem = snsList.find((each) => each.platform === platform);

    if (snsItem) {
      const baseUrl = snsItem.url.replace('{id}', '');

      if (url.startsWith(baseUrl)) {
        return url.substring(baseUrl.length);
      }
    }
  };

  return (
    <div
      className={cx(styles.snsLinkWrapper, !snsLinks?.length ? styles.noSnsLinks : '')}
      data-testid="profileDetails-snsLinks"
    >
      {isPC && !isViewingOtherUser && (
        <div className={styles.snsEditWrapper}>
          <EditButton
            className={styles.snsEdit}
            label={<Translate id="Review_Common.Edit" />}
            onClick={() => handleOpenSNS?.()}
          />
        </div>
      )}
      {snsLinks?.length ? (
        <div className={styles.snsLinkList} data-testid="profileDetails-snsLinkList">
          {snsLinks?.map((snsPlatform) => (
            <div className={styles.snsInfo} key={snsPlatform.platform}>
              <div className={styles.snsIcon}>{SNS_ICON_LIST[snsPlatform.platform]}</div>
              {snsPlatform.url && (
                <button
                  key={`sns-${snsPlatform.platform}`}
                  ref={ratLinkRef}
                  onClick={() => handleSnsUrl(snsPlatform.url)}
                  data-ratid={generateRatId(
                    ratTagValues.action,
                    ratTagValues.pageName,
                    isPC,
                    snsPlatform.platform,
                  )}
                  data-ratevent="click"
                  data-ratparam="all"
                  className={styles.snsLink}
                >
                  {getSNSId(snsPlatform.url, snsPlatform.platform)}
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          {isPC && !isViewingOtherUser && (
            <p className={styles.emptySns} data-testid="profileDetails-emptySns">
              <Translate id="MyReview.SNSEmpty" />
            </p>
          )}
        </>
      )}
    </div>
  );
}

const ProfileDetails = (props: ProfileDetailsProps) => {
  const { isPC } = useDeviceTypes();
  const {
    nickname,
    profileAvatar,
    coverImage,
    userBio,
    likeCount,
    reviewCount,
    viewCount,
    snsLinks,
    handleOpenSNS = () => {},
  } = props;
  const isViewingOtherUser = useAppSelector(getIsViewingOtherUser);
  const currentUserId = useAppSelector(isViewingOtherUser ? getOtherUserId : getUserId);
  const shareProfileUrl = `${DEFAULT_REVIEW_HOST}${paths.otherUserReviewPage.pathResolver(currentUserId)}`;
  const isUserRegistered = !!(profileAvatar?.url || userBio || coverImage?.url || snsLinks?.length);
  const shouldShowQandAButton = reviewCount > 0 && !isUserRegistered;

  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(shareProfileUrl);
    setShowCopiedToast(true);
  };

  const handleShare = () => {
    if ('share' in navigator) {
      navigator.share({
        url: shareProfileUrl,
      });
    }
  };

  const dispatch = useAppDispatch();

  const navigateToEditPage = () => {
    dispatch(pushLocation({ pathname: paths.editReviewPage.path }));
  };
  const openQandAPage = () => {
    window.open(MY_REVIEW_Q_AND_A_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className={styles.profileDetailsWrapper}>
        <div className={styles.coverImageSection}>
          {coverImage?.url ? (
            <Media
              media={{
                type: MediaType.IMAGE,
                url: coverImage?.url || '',
                alt: 'Cover image',
              }}
              wrapperClassName={styles.coverImageWrapper}
              className={styles.coverImage}
              isDisableSrcSetCache
              data-testid="profileDetails-coverImage"
            />
          ) : (
            <div className={styles.noCoverImage} />
          )}
          <>
            {!isViewingOtherUser && (
              <IconButton
                className={styles.questionIcon}
                onClick={openQandAPage}
                data-testid="profileDetails-QandA"
                aria-label="Q and A"
              >
                <HelpRexOutlineIcon size={20} />
              </IconButton>
            )}
            {isPC ? (
              <IconButton
                className={styles.share}
                onClick={handleCopyUrl}
                aria-label="Share Profile"
              >
                <PreviewLink size={20} />
              </IconButton>
            ) : (
              <IconButton className={styles.share} onClick={handleShare} aria-label="Share Profile">
                <ShareLarge size={20} />
              </IconButton>
            )}
          </>
        </div>
        <div className={styles.userProfileInfo}>
          <div className={styles.profileAvatar}>
            <div className={styles.iconBorder}>
              {profileAvatar?.url ? (
                <Media
                  media={{
                    type: MediaType.IMAGE,
                    url: profileAvatar.url,
                    alt: 'profileIcon',
                  }}
                  className={styles.avatarIcon}
                  wrapperClassName={styles.avatarWrapper}
                  isDisableSrcSetCache
                />
              ) : (
                <DefaultProfile className={styles.avatarIcon} />
              )}
            </div>
            {!isViewingOtherUser && (
              <>
                {shouldShowQandAButton ? (
                  <Button
                    variant={ButtonVariantEnum.Outlined}
                    size={ButtonSize.Medium}
                    onClick={navigateToEditPage}
                  >
                    <IconTextLink
                      icon={<EditVariant className={styles.edit} size={20} />}
                      text={<Translate id="MyReview.UpdateProfile" />}
                      className={styles.questionText}
                    ></IconTextLink>
                  </Button>
                ) : (
                  <EditButton
                    onClick={navigateToEditPage}
                    label={<Translate id="Review_Common.Edit" />}
                    iconSize={isPC ? 18 : 14}
                  />
                )}
              </>
            )}
          </div>
          <div className={styles.profileBioAndStats}>
            <div className={styles.profileBio}>
              {nickname && (
                <h1 className={styles.nickName} data-testid="profileDetails-nickname">
                  {nickname}
                </h1>
              )}
              {userBio && (
                <p className={styles.profileText} data-testid="profileDetails-userBio">
                  {userBio}
                </p>
              )}
            </div>
            {!isPC && snsLinks?.length ? (
              <UserProfileSNS snsLinks={snsLinks} handleOpenSNS={handleOpenSNS} />
            ) : null}
            <div className={styles.profileStats}>
              <ProfileStatItem
                count={reviewCount}
                label={<Translate id="MyReview.NumberOfPost" />}
                testId="profileDetails-reviewCount"
              />
              <ProfileStatItem
                count={viewCount}
                label={<Translate id="MyReview.ViewCount" />}
                testId="profileDetails-viewCount"
              />
              <ProfileStatItem
                count={likeCount}
                label={<Translate id="MyReview.LikeCount" />}
                testId="profileDetails-likeCount"
              />
            </div>
          </div>
          {isPC && <UserProfileSNS snsLinks={snsLinks} handleOpenSNS={handleOpenSNS} />}
        </div>
      </div>
      <NotificationBanner
        type={NotificationBannerTypes.Toast}
        state={NotificationBannerStates.Success}
        message={<Translate id="Review_Common.Copied" />}
        showToast={showCopiedToast}
        setShowToast={setShowCopiedToast}
        toastTimer={NotificationBannerToastTimers.Short}
        onClose={() => setShowCopiedToast(false)}
      />
    </>
  );
};

export default ProfileDetails;
